import { commonText, useCommonLanguage } from "../../i18n/commonPresentation";
/**
 * Select -- themed dropdown built on a custom listbox panel.
 *
 * Keeps the familiar `<option>` / `<optgroup>` children API so call sites read
 * like a plain select, while rendering a themed panel instead of the browser's
 * unstyleable native dropdown.
 *
 * A mirrored, visually hidden native `<select>` is kept in the tree so the
 * control still participates in forms (`name`, `required`, reset, validation)
 * and so `onChange` handlers receive a real select element as `event.target`.
 *
 * Keyboard behaviour -- arrows, Home/End, Enter, Escape and printable-character
 * type-ahead -- lives in `lib/select/selectKeyboard` and is unit tested there.
 */
import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../../utils/cn";
import {
  appendTypeAheadChar,
  initialHighlightIndex,
  isTypeAheadKey,
  resolveSelectKey,
  type SelectOptionModel,
} from "../../lib/select/selectKeyboard";
import { Icon } from "./Icon";
import { useAnchoredPosition } from "./useAnchoredPosition";

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> & {
  hasError?: boolean;
  /** Density variant: "default" for forms, "compact" for dense UIs */
  size?: "default" | "compact";
};

const sizeClasses = {
  default: "h-11 text-sm px-3 rounded-md",
  compact: "h-8 text-xs px-2.5 rounded",
} as const;

const optionTextClasses = {
  default: "text-sm",
  compact: "text-xs",
} as const;

// ---------------------------------------------------------------------------
// Children -> option model
// ---------------------------------------------------------------------------

/** Collapse an option's children into the plain text used for type-ahead. */
const flattenText = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenText).join("");
  if (isValidElement(node)) {
    const { children } = node.props as { children?: ReactNode };
    return flattenText(children);
  }
  return "";
};

interface ParsedOption extends SelectOptionModel {
  /** Original children, so rich option content still renders. */
  content: ReactNode;
}

/**
 * Walk `<option>` / `<optgroup>` children into a flat, ordered model.
 * Order matters: the flat index is what the keyboard contract navigates.
 */
const parseOptions = (children: ReactNode): ParsedOption[] => {
  const parsed: ParsedOption[] = [];

  const visit = (node: ReactNode, group?: string): void => {
    Children.toArray(node).forEach((child) => {
      if (!isValidElement(child)) return;

      if (child.type === Fragment) {
        visit((child.props as { children?: ReactNode }).children, group);
        return;
      }

      if (child.type === "optgroup") {
        const props = child.props as { label?: string; children?: ReactNode };
        visit(props.children, props.label);
        return;
      }

      if (child.type === "option") {
        const props = child.props as {
          value?: string | number | readonly string[];
          disabled?: boolean;
          children?: ReactNode;
        };
        const label = flattenText(props.children);
        parsed.push({
          value: props.value === undefined ? label : String(props.value),
          label,
          disabled: props.disabled === true,
          ...(group === undefined ? {} : { group }),
          content: props.children,
        });
      }
    });
  };

  visit(children);
  return parsed;
};

// ---------------------------------------------------------------------------
// Select
// ---------------------------------------------------------------------------

export const Select = ({
  className,
  hasError,
  size = "default",
  children,
  value,
  defaultValue,
  onChange,
  disabled,
  id,
  name,
  required,
  autoFocus,
  onBlur,
  ...rest
}: SelectProps) => {
  useCommonLanguage();
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hiddenRef = useRef<HTMLSelectElement>(null);
  const typeAhead = useRef({ buffer: "", at: 0 });
  const listboxId = useId();

  const options = useMemo(() => parseOptions(children), [children]);

  // `value` is the controlled source of truth; fall back to defaultValue so an
  // uncontrolled call site still shows the right label.
  const currentValue = String(value ?? defaultValue ?? "");
  const selected = useMemo(
    () => options.find((option) => option.value === currentValue),
    [options, currentValue],
  );

  const isDisabled = disabled === true;

  // -------------------------------------------------------------------------
  // Change emission
  // -------------------------------------------------------------------------

  /**
   * Emit through the mirrored native select so handlers get a real
   * `event.target` with a correct `.value`, matching the old behaviour.
   */
  const emitChange = useCallback(
    (nextValue: string) => {
      if (!onChange) return;
      const node = hiddenRef.current;
      if (node) node.value = nextValue;
      const target = node ?? ({ value: nextValue, name: name ?? "" } as HTMLSelectElement);
      onChange({
        target,
        currentTarget: target,
        type: "change",
        bubbles: true,
        cancelable: false,
        defaultPrevented: false,
        preventDefault: () => {},
        stopPropagation: () => {},
        isDefaultPrevented: () => false,
        isPropagationStopped: () => false,
        persist: () => {},
      } as unknown as ChangeEvent<HTMLSelectElement>);
    },
    [onChange, name],
  );

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option || option.disabled) return;
      emitChange(option.value);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [options, emitChange],
  );

  // -------------------------------------------------------------------------
  // Open / close
  // -------------------------------------------------------------------------

  const openAt = useCallback((index: number) => {
    setHighlightIndex(index);
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    if (isDisabled) return;
    setOpen((prev) => {
      if (prev) return false;
      setHighlightIndex(initialHighlightIndex(options, currentValue));
      return true;
    });
  }, [isDisabled, options, currentValue]);

  // Close when focus or a click lands outside the trigger and panel.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // A panel anchored to a trigger that has scrolled away is worse than none.
  const anchored = useAnchoredPosition({ open, anchor: triggerRef, panelRef });

  // Keep the active option in view for keyboard-only navigation.
  useEffect(() => {
    if (!open || highlightIndex < 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    panel.querySelectorAll<HTMLElement>("[role='option']")[highlightIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [open, highlightIndex]);

  // Focus deliberately stays on the trigger while the panel is open: the
  // panel is portaled to <body>, so focusing it would put the user in a
  // different place in the tab order than the control they are operating.
  // The active option is conveyed with `aria-activedescendant` instead.

  // -------------------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isDisabled) return;

      let buffer = "";
      if (isTypeAheadKey(event.key)) {
        const now = Date.now();
        buffer = appendTypeAheadChar(typeAhead.current.buffer, event.key, now - typeAhead.current.at);
        typeAhead.current = { buffer, at: now };
      }

      const action = resolveSelectKey({
        key: event.key,
        open,
        options,
        highlightIndex,
        value: currentValue,
        typeAheadBuffer: buffer,
      });

      if (action.type === "none") return;
      // Tab must keep its native focus move; everything else is ours.
      if (event.key !== "Tab") event.preventDefault();

      switch (action.type) {
        case "open":
          setOpen(true);
          break;
        case "open-at":
          openAt(action.index);
          break;
        case "highlight":
          setHighlightIndex(action.index);
          break;
        case "select":
          commit(action.index);
          break;
        case "close":
          if (event.key === "Tab") setOpen(false);
          else closePanel();
          break;
      }
    },
    [isDisabled, open, options, highlightIndex, currentValue, openAt, commit, closePanel],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const activeId = highlightIndex >= 0 ? `${listboxId}-opt-${highlightIndex}` : undefined;

  return (
    <>
      {/*
        Mirrored native select: carries form value/validation and acts as the
        event target. Hidden from assistive tech and the tab order -- the
        button below is the interactive control.
      */}
      <select
        ref={hiddenRef}
        aria-hidden
        tabIndex={-1}
        name={name}
        required={required}
        disabled={isDisabled}
        value={currentValue}
        onChange={() => {}}
        className="sr-only pointer-events-none absolute"
      >
        {/* Placeholder keeps an unmatched current value from being coerced
            into the first option, unless the caller supplies one already. */}
        {!options.some((option) => option.value === "") && <option value="" />}
        {options.map((option, index) => (
          <option key={`${option.value}-${index}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <button
        {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        autoFocus={autoFocus}
        disabled={isDisabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? activeId : undefined}
        aria-invalid={hasError === true ? true : undefined}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          // Focus leaving the trigger (Tab, or a click elsewhere) should not
          // strand an open panel behind.
          setOpen(false);
          (onBlur as ((e: typeof event) => void) | undefined)?.(event);
        }}
        className={cn(
          "inline-flex w-full items-center justify-between gap-2",
          "border border-border bg-input text-foreground",
          sizeClasses[size],
          "transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "focus-visible:bg-[rgb(var(--focus))]",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError && "border-destructive focus-visible:ring-destructive",
          className,
        )}
      >
        <span className={cn("truncate text-left", !selected && "text-muted-foreground")}>
          {selected ? selected.content : ""}
        </span>
        <Icon
          icon="lucide:chevron-down"
          className={cn(
            "shrink-0 text-muted-foreground transition-transform",
            size === "compact" ? "h-3.5 w-3.5" : "h-4 w-4",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {open &&
        createPortal(
          <div
            id={listboxId}
            ref={panelRef}
            role="listbox"
            // Keep the trigger focused when the panel is clicked, so the
            // control never loses focus mid-interaction.
            onMouseDown={(event) => event.preventDefault()}
            style={{
              ...anchored.style,
              minWidth: triggerRef.current?.offsetWidth,
            }}
            className={cn(
              "z-[9999] flex flex-col overflow-hidden",
              "max-h-[min(70vh,320px,var(--qb-available-h,100vh))] max-w-[var(--qb-available-w,100vw)]",
              "rounded-lg border border-border/60 bg-card/95 backdrop-blur-md",
              "text-foreground shadow-soft outline-none",
              anchored.className,
            )}
          >
            <div className="overflow-y-auto qb-scroll py-1">
              {options.length === 0 && (
                <div className="px-2.5 py-2 text-xs text-muted-foreground">{commonText("noOptions")}</div>
              )}

              {groupSequence(options).map((entry) => {
                if (entry.kind === "group-label") {
                  return (
                    <div
                      key={`group-${entry.label}`}
                      role="presentation"
                      className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {entry.label}
                    </div>
                  );
                }

                const { option, index } = entry;
                const isSelected = option.value === currentValue;
                const isHighlighted = index === highlightIndex;

                return (
                  <button
                    key={`${option.value}-${index}`}
                    id={`${listboxId}-opt-${index}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    disabled={option.disabled}
                    tabIndex={-1}
                    onClick={() => commit(index)}
                    onMouseEnter={() => {
                      if (!option.disabled) setHighlightIndex(index);
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors",
                      optionTextClasses[size],
                      option.group !== undefined && "pl-4",
                      isHighlighted && !option.disabled && "bg-muted/60",
                      isSelected && "font-medium text-primary-text",
                      option.disabled && "cursor-not-allowed opacity-40",
                    )}
                  >
                    <span className="flex-1 truncate">{option.content}</span>
                    {isSelected && (
                      <Icon icon="lucide:check" className="h-3 w-3 shrink-0 text-primary" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Render sequencing
// ---------------------------------------------------------------------------

type RenderEntry =
  | { kind: "group-label"; label: string }
  | { kind: "option"; option: ParsedOption; index: number };

/**
 * Interleave group headings with their options, carrying each option's flat
 * index so rendering and the keyboard contract agree on option identity.
 */
const groupSequence = (options: readonly ParsedOption[]): RenderEntry[] => {
  const entries: RenderEntry[] = [];
  let currentGroup: string | undefined;

  options.forEach((option, index) => {
    if (option.group !== undefined && option.group !== currentGroup) {
      entries.push({ kind: "group-label", label: option.group });
    }
    currentGroup = option.group;
    entries.push({ kind: "option", option, index });
  });

  return entries;
};
